import { ChatMessage } from '../types';
import { buildPDFFromImages } from './whiteboardExport';

/**
 * Loads an image from a URL or Data URI asynchronously.
 */
function loadImage(src: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

/**
 * Helper to wrap text into multiple lines within a maximum pixel width.
 */
function wrapTextLines(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
  const words = text.split(/\s+/);
  const lines: string[] = [];
  let currentLine = '';

  for (const word of words) {
    const testLine = currentLine ? `${currentLine} ${word}` : word;
    const metrics = ctx.measureText(testLine);
    if (metrics.width > maxWidth && currentLine) {
      lines.push(currentLine);
      currentLine = word;
    } else {
      currentLine = testLine;
    }
  }
  if (currentLine) {
    lines.push(currentLine);
  }
  return lines;
}

export interface ChatExportOptions {
  roomId: string;
  title?: string;
  exportedBy?: string;
}

/**
 * Export all meeting chat messages to a crystal-clear, high-resolution PDF (A4 300 DPI)
 * with embedded full-resolution screenshots and image attachments.
 */
export async function exportChatToPDF(
  messages: ChatMessage[],
  opts: ChatExportOptions
): Promise<void> {
  // A4 Standard at 300 DPI high resolution
  const PAGE_WIDTH = 2480;
  const PAGE_HEIGHT = 3508;
  const MARGIN_X = 140;
  const MARGIN_TOP = 140;
  const MARGIN_BOTTOM = 140;
  const USABLE_WIDTH = PAGE_WIDTH - MARGIN_X * 2;
  const MAX_Y = PAGE_HEIGHT - MARGIN_BOTTOM;

  // Pre-load all screenshot images in high resolution
  const imageMap = new Map<string, HTMLImageElement>();
  for (const msg of messages) {
    if (msg.attachment_url) {
      const isImg = msg.attachment_type === 'image' || !!msg.attachment_name?.match(/\.(png|jpe?g|gif|webp|svg)$/i);
      if (isImg && !imageMap.has(msg.attachment_url)) {
        const loaded = await loadImage(msg.attachment_url);
        if (loaded) {
          imageMap.set(msg.attachment_url, loaded);
        }
      }
    }
  }

  const pageCanvases: HTMLCanvasElement[] = [];

  function createNewPage(): { canvas: HTMLCanvasElement; ctx: CanvasRenderingContext2D } {
    const canvas = document.createElement('canvas');
    canvas.width = PAGE_WIDTH;
    canvas.height = PAGE_HEIGHT;
    const ctx = canvas.getContext('2d')!;

    // High quality rendering
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    // White background
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, PAGE_WIDTH, PAGE_HEIGHT);

    // Decorative top accent bar
    const gradient = ctx.createLinearGradient(0, 0, PAGE_WIDTH, 0);
    gradient.addColorStop(0, '#6366f1');
    gradient.addColorStop(0.5, '#3b82f6');
    gradient.addColorStop(1, '#06b6d4');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, PAGE_WIDTH, 14);

    pageCanvases.push(canvas);
    return { canvas, ctx };
  }

  let { ctx } = createNewPage();
  let currentY = MARGIN_TOP;

  // 1. Draw Cover / Document Header
  ctx.fillStyle = '#1e1b4b';
  ctx.font = 'bold 52px system-ui, -apple-system, sans-serif';
  ctx.fillText('VERVE — MEETING CHAT TRANSCRIPT', MARGIN_X, currentY);
  currentY += 46;

  ctx.fillStyle = '#64748b';
  ctx.font = '500 28px system-ui, -apple-system, sans-serif';
  const meetingTitle = opts.title || 'Verve Instant Video Conference';
  ctx.fillText(`Meeting: ${meetingTitle}  •  Room Code: ${opts.roomId}`, MARGIN_X, currentY);
  currentY += 36;

  const exportDateStr = new Date().toLocaleString([], {
    dateStyle: 'full',
    timeStyle: 'medium',
  });
  ctx.fillText(`Exported On: ${exportDateStr}  •  Total Messages: ${messages.length}`, MARGIN_X, currentY);
  currentY += 44;

  // Header separator line
  ctx.strokeStyle = '#e2e8f0';
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.moveTo(MARGIN_X, currentY);
  ctx.lineTo(PAGE_WIDTH - MARGIN_X, currentY);
  ctx.stroke();
  currentY += 50;

  // 2. Render each message
  for (let idx = 0; idx < messages.length; idx++) {
    const msg = messages[idx];
    const timeStr = new Date(msg.timestamp * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    const isDirect = !!msg.is_private;
    const attachedImg = msg.attachment_url ? imageMap.get(msg.attachment_url) : null;

    // Calculate message text lines
    ctx.font = '28px system-ui, -apple-system, sans-serif';
    const textLines = msg.content ? wrapTextLines(ctx, msg.content, USABLE_WIDTH - 60) : [];
    const textHeight = textLines.length * 40;

    // Calculate attachment image dimensions if present
    let imgDrawW = 0;
    let imgDrawH = 0;
    if (attachedImg) {
      const maxImgW = USABLE_WIDTH - 60;
      const maxImgH = 1100;
      const aspect = attachedImg.naturalWidth / attachedImg.naturalHeight;
      imgDrawW = Math.min(maxImgW, attachedImg.naturalWidth);
      imgDrawH = imgDrawW / aspect;
      if (imgDrawH > maxImgH) {
        imgDrawH = maxImgH;
        imgDrawW = imgDrawH * aspect;
      }
    }

    const fileMetaHeight = (!attachedImg && msg.attachment_name) ? 60 : 0;
    const cardContentHeight = 60 + textHeight + (imgDrawH ? imgDrawH + 60 : 0) + fileMetaHeight + 30;

    // If card exceeds page height, begin new page
    if (currentY + cardContentHeight > MAX_Y) {
      const newPage = createNewPage();
      ctx = newPage.ctx;
      currentY = MARGIN_TOP;

      // Small top page continuation header
      ctx.fillStyle = '#94a3b8';
      ctx.font = 'bold 24px system-ui, -apple-system, sans-serif';
      ctx.fillText(`VERVE MEETING CHAT TRANSCRIPT  •  Room: ${opts.roomId} (Continued)`, MARGIN_X, currentY);
      currentY += 40;
      ctx.strokeStyle = '#f1f5f9';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(MARGIN_X, currentY);
      ctx.lineTo(PAGE_WIDTH - MARGIN_X, currentY);
      ctx.stroke();
      currentY += 40;
    }

    // Draw message container card
    const cardX = MARGIN_X;
    const cardY = currentY;
    const cardW = USABLE_WIDTH;
    const cardH = cardContentHeight;

    ctx.fillStyle = isDirect ? '#f5f3ff' : '#f8fafc';
    ctx.strokeStyle = isDirect ? '#c4b5fd' : '#e2e8f0';
    ctx.lineWidth = 2;

    // Rounded rectangle
    roundRect(ctx, cardX, cardY, cardW, cardH, 20);
    ctx.fill();
    ctx.stroke();

    // Message Header: Sender name, direct badge, and timestamp
    let innerY = cardY + 45;
    const innerX = cardX + 30;

    // Sender Avatar circle
    ctx.fillStyle = isDirect ? '#8b5cf6' : '#3b82f6';
    ctx.beginPath();
    ctx.arc(innerX + 18, innerY - 10, 20, 0, Math.PI * 2);
    ctx.fill();

    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 22px system-ui, -apple-system, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    const initial = (msg.sender_name[0] || 'U').toUpperCase();
    ctx.fillText(initial, innerX + 18, innerY - 10);

    // Reset alignment
    ctx.textAlign = 'left';
    ctx.textBaseline = 'alphabetic';

    // Sender Name
    ctx.fillStyle = '#0f172a';
    ctx.font = 'bold 30px system-ui, -apple-system, sans-serif';
    ctx.fillText(msg.sender_name, innerX + 50, innerY);

    const nameWidth = ctx.measureText(msg.sender_name).width;

    // Direct message badge
    if (isDirect) {
      ctx.fillStyle = '#7c3aed';
      ctx.font = 'bold 20px system-ui, -apple-system, sans-serif';
      const badgeX = innerX + 50 + nameWidth + 16;
      roundRect(ctx, badgeX, innerY - 24, 170, 32, 8);
      ctx.fill();
      ctx.fillStyle = '#ffffff';
      ctx.fillText('DIRECT MESSAGE', badgeX + 12, innerY - 2);
    }

    // Timestamp
    ctx.fillStyle = '#94a3b8';
    ctx.font = '500 24px system-ui, -apple-system, sans-serif';
    const timeWidth = ctx.measureText(timeStr).width;
    ctx.fillText(timeStr, cardX + cardW - 30 - timeWidth, innerY);

    innerY += 40;

    // Message Text
    if (textLines.length > 0) {
      ctx.fillStyle = '#334155';
      ctx.font = '28px system-ui, -apple-system, sans-serif';
      for (const line of textLines) {
        ctx.fillText(line, innerX, innerY);
        innerY += 40;
      }
      innerY += 10;
    }

    // High-Resolution Screenshot / Image Attachment
    if (attachedImg) {
      const imgX = innerX;
      const imgY = innerY;

      // Draw subtle shadow / border behind screenshot
      ctx.fillStyle = '#000000';
      ctx.save();
      ctx.shadowColor = 'rgba(0,0,0,0.12)';
      ctx.shadowBlur = 16;
      ctx.shadowOffsetY = 6;

      ctx.strokeStyle = '#cbd5e1';
      ctx.lineWidth = 2;
      roundRect(ctx, imgX, imgY, imgDrawW, imgDrawH, 12);
      ctx.stroke();
      ctx.restore();

      // Clip image to rounded rectangle
      ctx.save();
      ctx.beginPath();
      roundRect(ctx, imgX, imgY, imgDrawW, imgDrawH, 12);
      ctx.clip();
      ctx.drawImage(attachedImg, imgX, imgY, imgDrawW, imgDrawH);
      ctx.restore();

      innerY += imgDrawH + 34;

      // Image Caption
      ctx.fillStyle = '#64748b';
      ctx.font = 'italic 22px system-ui, -apple-system, sans-serif';
      ctx.fillText(`📷 Attached Screenshot: ${msg.attachment_name || 'Screenshot'} (High Resolution)`, innerX, innerY);
      innerY += 24;
    } else if (msg.attachment_name) {
      // Document file attachment
      ctx.fillStyle = '#f1f5f9';
      ctx.strokeStyle = '#cbd5e1';
      ctx.lineWidth = 1.5;
      roundRect(ctx, innerX, innerY, USABLE_WIDTH - 60, 48, 8);
      ctx.fill();
      ctx.stroke();

      ctx.fillStyle = '#334155';
      ctx.font = 'bold 22px system-ui, -apple-system, sans-serif';
      ctx.fillText(`📄 File Attachment: ${msg.attachment_name}`, innerX + 16, innerY + 32);
      innerY += 60;
    }

    currentY += cardH + 28;
  }

  // 3. Draw Footers on all pages
  const totalPages = pageCanvases.length;
  for (let pIdx = 0; pIdx < totalPages; pIdx++) {
    const pCanvas = pageCanvases[pIdx];
    const pCtx = pCanvas.getContext('2d')!;

    pCtx.strokeStyle = '#e2e8f0';
    pCtx.lineWidth = 2;
    pCtx.beginPath();
    pCtx.moveTo(MARGIN_X, PAGE_HEIGHT - MARGIN_BOTTOM + 40);
    pCtx.lineTo(PAGE_WIDTH - MARGIN_X, PAGE_HEIGHT - MARGIN_BOTTOM + 40);
    pCtx.stroke();

    pCtx.fillStyle = '#94a3b8';
    pCtx.font = '500 22px system-ui, -apple-system, sans-serif';
    pCtx.fillText('Verve Video Conferencing — High-Resolution Confidential Chat Record', MARGIN_X, PAGE_HEIGHT - MARGIN_BOTTOM + 75);

    const pageNumText = `Page ${pIdx + 1} of ${totalPages}`;
    const pageNumWidth = pCtx.measureText(pageNumText).width;
    pCtx.fillText(pageNumText, PAGE_WIDTH - MARGIN_X - pageNumWidth, PAGE_HEIGHT - MARGIN_BOTTOM + 75);
  }

  // 4. Convert all page canvases to JPEG images
  const pageImages: string[] = [];
  for (const canvas of pageCanvases) {
    pageImages.push(canvas.toDataURL('image/jpeg', 0.95));
  }

  // 5. Construct Multi-Page PDF bytes (A4: 595 x 842 points)
  const pdfBytes = buildPDFFromImages(pageImages, 595, 842, PAGE_WIDTH, PAGE_HEIGHT);

  // 6. Trigger direct download
  const blob = new Blob([pdfBytes.buffer as ArrayBuffer], { type: 'application/pdf' });
  const downloadUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = downloadUrl;
  const cleanCode = opts.roomId.replace(/[^a-zA-Z0-9_-]/g, '');
  a.download = `verve-chat-${cleanCode}-${Date.now()}.pdf`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(downloadUrl), 5000);
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number
) {
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + width - radius, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
  ctx.lineTo(x + width, y + height - radius);
  ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
  ctx.lineTo(x + radius, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
  ctx.lineTo(x, y + radius);
  ctx.quadraticCurveTo(x, y, x + radius, y);
  ctx.closePath();
}
